import { useEffect, useState } from 'react';
import { Box, Button, Divider, SelectChangeEvent, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import CustomModal from '../Modal/Modal';
import { APPOINTMENTS_ROUTE } from '../../utils/routes';
import CustomDatePicker from '../DatePicker/DatePicker';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import TimePicker from '../TimePicker/TimePicker';
import ClientsDropdown, { ISelectOption } from '../ClientsDropdown/ClientsDropdown';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { createAppointmentRequest } from '../../redux/appointment/appointmentSlice';
import LabeledInput from '../LabeledInput/LabeledInput';
import { appointmentPlatformOptions, meetingDurationOptions } from '../../types/appointments';

function AddAppointmentModal() {
  const dispatch = useAppDispatch();
  const [date, setDate] = useState<null | string>(null);
  const [time, setTime] = useState<null | string>(null);
  const [client, setClient] = useState<null | ISelectOption>(null);
  const [platform, setPlatform] = useState<string>('ZOOM');
  const [isFormValid, setIsFormValid] = useState(false);
  const [title, setTitle] = useState<string>('');
  const [meetingDuration, setMeetingDuration] = useState<string>('30');
  const coach = useAppSelector((state) => state.coach.coach);
  const coachId = coach?.id || '';

  // simple validation
  const validateForm = () => date && time && client && platform && title && meetingDuration;

  const navigate = useNavigate();
  useEffect(() => {
    setIsFormValid(validateForm() !== null);
  }, [date, time, client, platform]);

  const handleAddAppointment = () => {
    if (!client || !date) {
      return;
    }
    const combinedStartDateTime = dayjs(`${date} ${time}`);
    const startDateTime = combinedStartDateTime.toISOString();
    const endDateTime = dayjs(startDateTime).add(Number(meetingDuration), 'minute').toISOString();
    dispatch(
      createAppointmentRequest({
        clientId: client?.id,
        coachId,
        startDate: startDateTime,
        endDate: endDateTime,
        title,
        description: 'description',
        platform,
        meetingURL: '',
      })
    );
    navigate(APPOINTMENTS_ROUTE);
  };

  const handleClientChange = (event: ISelectOption) => {
    setClient(event);
  };

  const handlePlatformChange = (event: SelectChangeEvent<unknown>) => {
    setPlatform(event.target.value as string);
  };

  return (
    <CustomModal
      isOpen
      onClose={() => {
        navigate(APPOINTMENTS_ROUTE);
      }}
    >
      <Box width="486px" gap="36px" p="32px" display="flex" flexDirection="column">
        <Box display="flex" justifyContent="space-between">
          <Typography variant="h4" color="primary">
            Create Appointment
          </Typography>
          <img
            src="/assets/icons/x-orange.svg"
            alt="close"
            onClick={() => navigate(APPOINTMENTS_ROUTE)}
            role="presentation"
            style={{
              cursor: 'pointer',
              width: '24px',
              height: '24px',
            }}
          />
        </Box>
        <Divider />
        <Box display="flex" gap="36px" flexDirection="column">
          <Box>
            <LabeledInput
              label="Meeting Title"
              id="meeting-title"
              type="text"
              value={title}
              handleChange={(event) => setTitle(event.target.value)}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" gap="5px">
            {/* date time */}
            <Box>
              <Typography variant="meta" color="primary">
                Date
              </Typography>
              <CustomDatePicker
                date={date || ''}
                handleOneTimeChange={(_field, value: string) => {
                  setDate(value);
                }}
              />
            </Box>
            <Box>
              <Typography variant="meta">Time</Typography>
              <TimePicker
                time={time || ''}
                handleOneTimeChange={(_field, value: string) => {
                  setTime(value);
                }}
              />
            </Box>
            <Box>
              <Typography variant="meta">Duration</Typography>
              <CustomDropdown
                options={meetingDurationOptions}
                label="duration"
                value={meetingDuration}
                onChange={(event: SelectChangeEvent<unknown>) => {
                  setMeetingDuration(event.target.value as string);
                }}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="meta">Client</Typography>
            <ClientsDropdown handleClientChange={handleClientChange} selectedClient={client} />
          </Box>
          <Box>
            {/* platform */}
            <Typography variant="meta" color="primary">
              Appointment Platform
            </Typography>
            <CustomDropdown
              options={appointmentPlatformOptions}
              label="platform"
              value={platform}
              onChange={handlePlatformChange}
            />
          </Box>
        </Box>
        <Divider />
        <Box display="flex" flexDirection="row-reverse">
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddAppointment}
            disabled={!isFormValid}
          >
            Add Client
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
}

export default AddAppointmentModal;
